/**
 * admin-svc /admin/brokerage-settings — the terms the brokerage's agreements are
 * written on (V055).
 *
 * A TXR-1501 states a market area (¶3C), a term (¶4), the broker's fee (¶7A), a
 * protection period (¶7F) and the county amounts are payable in (¶7H). Those are the
 * SAME on every agreement the brokerage sends, so they are settings — edited here,
 * read by trec-svc when it fills the form. Hardcoding them would have meant a deploy
 * to correct a fee, and a second brokerage sending the first one's terms.
 */

import type { ApiClient } from "../client";

export interface BrokerageSettings {
  /** The owning tenant's type — gates TREC-only UI (IABS/CPN) off MORTGAGE. */
  brokerage_type: "REAL_ESTATE" | "MORTGAGE" | "";
  /** Read-only tenant facts the server attaches — they live on `tenants` /
   * `tenant_domains`, and the routes that otherwise serve them are
   * super_admin-only, so this is a brokerage_admin's only view of them. */
  display_name: string;
  status: string;
  custom_domain: string | null;
  custom_domain_verified: boolean;
  legal_name: string;
  trec_license_number: string | null;
  /** The two people the IABS names, with their own licence numbers (V082).
   *  A blank supervisor means "the designated broker" — trec-svc resolves that
   *  fallback at prefill rather than the API copying the value. */
  designated_broker_name: string | null;
  designated_broker_license_no: string | null;
  supervising_license_holder: string | null;
  supervising_license_no: string | null;
  /** …and how to reach them (V092). The IABS prints an email and phone on each
   *  of their rows; before this it printed the FIRM's on both. The designated
   *  broker's pair is required to activate; the supervisor's is not. */
  designated_broker_email: string | null;
  designated_broker_phone: string | null;
  supervising_email: string | null;
  supervising_phone: string | null;
  /** NMLS ID — a MORTGAGE brokerage's identity, required before it can activate. */
  nmls_id: string | null;
  support_email: string | null;
  primary_phone: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  /** ¶3C — the form itself says this should NOT be left blank. */
  market_area: string | null;
  /** ¶7H — ships as "NA" on the form when unset. */
  county: string | null;
  /** ¶7A(1) — % of the sales price. */
  purchase_fee_pct: string;
  /** ¶7A(2) — % of ONE MONTH'S rent; 100 = the customary one month. */
  lease_fee_pct: string;
  /**
   * TXR 1102 ¶5.E(1) / ¶5.E(2) — the renewal and sale compensation on a LEASE
   * listing agreement. Nullable, and null means INHERIT: the renewal rate falls
   * back to the lease fee and the sale rate to the purchase fee. These are the
   * override. Null is not zero — an explicit 0 means "charges nothing".
   */
  renewal_fee_pct: string | null;
  sale_fee_pct: string | null;
  /** ¶7F — days after the agreement ends during which a fee is still owed. */
  protection_period_days: number;
  /** ¶4 — the term begins the day it is sent and runs this many months. */
  representation_term_months: number;
  /** V002 branding, unused until PR A — hex colors, e.g. "#1A2B3C". */
  primary_color: string | null;
  secondary_color: string | null;
  /** Raw S3 key, not a URL — the public landing page gets a presigned GET instead. */
  logo_s3_key: string | null;
  /** Short-lived presigned GET for the admin's own preview (server-attached, not stored). */
  logo_url: string | null;
}

export type BrokerageSettingsUpdate = Partial<BrokerageSettings>;

export interface LogoUploadUrlResponse {
  upload_url: string;
  content_type: string;
  s3_key: string;
}

/** `?tenant_id=` lets a super_admin read/write ANOTHER tenant's settings — they have
 * no brokerage of their own. A brokerage_admin omits it and edits their own row. */
function path(tenantId?: string) {
  return tenantId
    ? `/admin/brokerage-settings?tenant_id=${encodeURIComponent(tenantId)}`
    : "/admin/brokerage-settings";
}

function logoPath(action: "presign" | "confirm", tenantId?: string) {
  const base = `/admin/brokerage-logo/${action}`;
  return tenantId ? `${base}?tenant_id=${encodeURIComponent(tenantId)}` : base;
}

export const brokerageSettingsService = {
  get(client: ApiClient, tenantId?: string) {
    return client.request<BrokerageSettings>(path(tenantId));
  },

  update(client: ApiClient, body: BrokerageSettingsUpdate, tenantId?: string) {
    return client.request<BrokerageSettings>(path(tenantId), {
      method: "PATCH",
      body,
    });
  },

  /** Step 1 of a logo upload: a presigned PUT for the browser to send the file to. */
  presignLogoUpload(client: ApiClient, ext: string, tenantId?: string) {
    return client.request<LogoUploadUrlResponse>(
      logoPath("presign", tenantId),
      {
        method: "POST",
        body: { ext },
      },
    );
  },

  /** Step 2: after the PUT succeeds, confirm — the server enforces the size cap
   * and saves the key onto the brokerage row. */
  confirmLogoUpload(client: ApiClient, s3Key: string, tenantId?: string) {
    return client.request<BrokerageSettings>(logoPath("confirm", tenantId), {
      method: "POST",
      body: { s3_key: s3Key },
    });
  },
};

/**
 * PUT the logo file straight to S3. `contentType` must match exactly what
 * `presignLogoUpload` returned — it is part of the presigned signature.
 */
export async function uploadLogoFile(
  uploadUrl: string,
  contentType: string,
  file: Blob,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Logo upload failed (${response.status})`);
  }
}
